import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  aiSetupStart,
  aiSetupAnalyze,
  aiSetupSubmitStep2,
  aiSetupProposeIntents,
  aiSetupConfirmIntents,
  aiSetupIntentDataStart,
  aiSetupSubmitStep4,
  aiSetupStructureContext,
  aiSetupGenerate,
  aiSetupGetGenerationStatus,
  aiSetupGetSession,
  aiSetupGetActive,
  isStepConflictError,
  type IntentProposalItem,
  type SessionResponse,
  type GenerationStatusResponse,
  type Step2Answer,
  type Step4Answer,
} from '../services/aiSetupApi';

type Step =
  | 'idle'
  | 'analysis_interview'
  | 'intent_proposal'
  | 'intent_data_collection'
  | 'structuring_context'
  | 'generation_configuration'
  | 'generating_in_progress'
  | 'completed'
  | 'cancelled'
  | 'generation_failed';

const POLL_INTERVAL_MS = 2000;

export const AISetupWizardPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const projectIdNum = projectId ? parseInt(projectId, 10) : null;

  const [step, setStep] = useState<Step>('idle');
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [chatHistory, setChatHistory] = useState<Array<{ role: string; content: string }>>([]);
  const [, setCompanyProfile] = useState<Record<string, unknown> | null>(null);
  const [questions, setQuestions] = useState<string[] | null>(null);
  const [intents, setIntents] = useState<IntentProposalItem[]>([]);
  const [step2Answers, setStep2Answers] = useState<Record<number, { answer: string; skipped: boolean }>>({});
  const [intentDataQuestions, setIntentDataQuestions] = useState<string[] | null>(null);
  const [step4Answers, setStep4Answers] = useState<Record<number, { answer: string; skipped: boolean; in_knowledge_base: boolean }>>({});
  const [batchId, setBatchId] = useState<string | null>(null);
  const [batchStatus, setBatchStatus] = useState<GenerationStatusResponse | null>(null);

  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshSession = useCallback(
    async (stepsId: number) => {
      if (!projectIdNum) return null;
      try {
        const s = await aiSetupGetSession(projectIdNum, stepsId);
        setSession(s);
        setStep(s.step as Step);
        setChatHistory(s.chat_history || []);
        setCompanyProfile((s.collected_data as Record<string, unknown>)?.company_profile as Record<string, unknown> ?? null);
        const cd = s.collected_data as Record<string, unknown> | undefined;
        const step2Q = cd?.step2_questions as string[] | undefined;
        const step4Q = cd?.step4_questions as string[] | undefined;
        if (s.step === 'analysis_interview') {
          setQuestions(step2Q ?? null);
        } else if (s.step === 'intent_data_collection') {
          setQuestions(step4Q ?? null);
        } else {
          setQuestions(null);
        }
        const confirmed = cd?.intents_confirmed as IntentProposalItem[] | undefined;
        if (confirmed && confirmed.length > 0) {
          setIntents(confirmed);
        }
        const proposed = cd?.intents_proposed as IntentProposalItem[] | undefined;
        const intentsDraft = cd?.intents_draft as IntentProposalItem[] | undefined;
        if (proposed && proposed.length > 0 && (!confirmed || confirmed.length === 0)) {
          setIntents(proposed);
        } else if (intentsDraft && intentsDraft.length > 0 && (!confirmed || confirmed.length === 0) && !proposed) {
          setIntents(intentsDraft);
        }
        if (s.step === 'intent_data_collection' && step4Q && step4Q.length > 0) {
          setIntentDataQuestions(step4Q);
        } else if (s.step !== 'intent_data_collection') {
          setIntentDataQuestions(null);
        }
        return s;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load session');
        return null;
      }
    },
    [projectIdNum]
  );

  const handleStepConflict = useCallback(async () => {
    if (sessionId) await refreshSession(sessionId);
  }, [sessionId, refreshSession]);

  const bootstrap = useCallback(async () => {
    if (!projectIdNum) return;
    setLoading(true);
    setError(null);
    try {
      const active = await aiSetupGetActive(projectIdNum);
      if (active.active && active.session_id != null) {
        setSessionId(active.session_id);
        if (active.step) setStep(active.step as Step);
        const s = await refreshSession(active.session_id);
        if (s?.step === 'analysis_interview') {
          const cd = s.collected_data as Record<string, unknown> | undefined;
          const raw = cd?.company_description_raw;
          if (typeof raw === 'string' && raw.trim()) setInputValue(raw.trim());
        }
        if (s && (s.step === 'generating_in_progress' || s.step === 'completed' || s.step === 'generation_failed' || s.step === 'structuring_context' || s.step === 'intent_data_collection')) {
          const cd = s.collected_data as Record<string, unknown>;
          const bid = cd?.generation_batch_id as string | undefined;
          if (bid) setBatchId(bid);
          if (s.step === 'completed' || s.step === 'generation_failed') {
            setStep(s.step as Step);
          }
        }
      } else {
        setStep('idle');
        setSessionId(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load wizard state');
    } finally {
      setLoading(false);
    }
  }, [projectIdNum, refreshSession]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const intentDataInitRef = React.useRef(false);
  useEffect(() => {
    const cd = session?.collected_data as { step4_questions?: string[] } | undefined;
    const hasQuestions = cd?.step4_questions && cd.step4_questions.length > 0;
    if (step === 'intent_data_collection' && projectIdNum && sessionId && !intentDataInitRef.current && !loading && !hasQuestions) {
      intentDataInitRef.current = true;
      aiSetupIntentDataStart(projectIdNum, sessionId).then(
        (res) => {
          setSessionId(res.session_id);
          setStep(res.step as Step);
          setIntentDataQuestions(res.questions ?? null);
          setStep4Answers({});
          if (res.done) setStep('structuring_context' as Step);
        },
        (e) => {
          intentDataInitRef.current = false;
          if (isStepConflictError(e)) handleStepConflict();
          else setError(e instanceof Error ? e.message : 'Failed to start');
        }
      ).finally(() => setLoading(false));
      setLoading(true);
    }
    if (step !== 'intent_data_collection') intentDataInitRef.current = false;
  }, [step, projectIdNum, sessionId, session, handleStepConflict]);

  const structureInitRef = React.useRef(false);
  useEffect(() => {
    if (step === 'structuring_context' && projectIdNum && sessionId && !structureInitRef.current && !loading) {
      structureInitRef.current = true;
      aiSetupStructureContext(projectIdNum, sessionId).then(
        (res) => {
          setSessionId(res.session_id);
          setStep(res.step as Step);
        },
        (e) => {
          structureInitRef.current = false;
          if (isStepConflictError(e)) handleStepConflict();
          else setError(e instanceof Error ? e.message : 'Failed to structure');
        }
      ).finally(() => setLoading(false));
      setLoading(true);
    }
    if (step !== 'structuring_context') structureInitRef.current = false;
  }, [step, projectIdNum, sessionId, handleStepConflict]);

  const clearError = () => setError(null);

  const handleStart = async (forceRestart = false) => {
    if (!projectIdNum) return;
    setLoading(true);
    setError(null);
    try {
      const res = await aiSetupStart(projectIdNum, forceRestart);
      setSessionId(res.session_id);
      setStep(res.step as Step);
      setChatHistory([]);
      setQuestions(null);
      setCompanyProfile(null);
      setIntents([]);
    } catch (e) {
      if (isStepConflictError(e)) {
        const active = await aiSetupGetActive(projectIdNum);
        if (active.active && active.session_id != null) {
          setSessionId(active.session_id);
          if (active.step) setStep(active.step as Step);
          const s = await refreshSession(active.session_id);
          if (s?.step === 'analysis_interview') {
            const cd = s.collected_data as Record<string, unknown> | undefined;
            const raw = cd?.company_description_raw;
            if (typeof raw === 'string' && raw.trim()) setInputValue(raw.trim());
          }
          setError(null);
        } else {
          await handleStepConflict();
        }
      } else {
        setError(e instanceof Error ? e.message : 'Failed to start');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!projectIdNum || !inputValue.trim()) return;
    const text = inputValue.trim();
    setInputValue('');
    setLoading(true);
    setError(null);
    try {
      const res = await aiSetupAnalyze(projectIdNum, text, sessionId ?? undefined);
      setSessionId(res.session_id);
      setStep(res.step as Step);
      setCompanyProfile(res.company_profile ?? null);
      setQuestions(res.questions ?? null);
      setStep2Answers({});
      setChatHistory((prev) => [...prev, { role: 'user', content: text }]);
    } catch (e) {
      if (isStepConflictError(e)) {
        await handleStepConflict();
      } else {
        setError(e instanceof Error ? e.message : 'Failed to analyze');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStep2 = async () => {
    if (!projectIdNum) return;
    setLoading(true);
    setError(null);
    try {
      const qList = questions ?? [];
      const answers: Step2Answer[] = qList.map((_, i) => {
        const a = step2Answers[i];
        return {
          question_index: i,
          answer: a?.skipped ? undefined : (a?.answer ?? undefined),
          skipped: a?.skipped ?? true,
        };
      });
      const res = await aiSetupSubmitStep2(projectIdNum, answers, sessionId ?? undefined);
      setSessionId(res.session_id);
      setStep(res.step as Step);
      setIntents(res.intents);
      setQuestions(null);
      setStep2Answers({});
    } catch (e) {
      if (isStepConflictError(e)) {
        await handleStepConflict();
      } else {
        setError(e instanceof Error ? e.message : 'Failed to submit');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProposeIntents = async () => {
    if (!projectIdNum) return;
    setLoading(true);
    setError(null);
    try {
      const res = await aiSetupProposeIntents(projectIdNum, sessionId ?? undefined);
      setSessionId(res.session_id);
      setStep(res.step as Step);
      setIntents(res.intents);
    } catch (e) {
      if (isStepConflictError(e)) {
        await handleStepConflict();
      } else {
        setError(e instanceof Error ? e.message : 'Failed to propose intents');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmIntents = async () => {
    if (!projectIdNum || intents.length === 0) return;
    const hasInvalid = intents.some((i) => !(i.display_name || '').trim() || !(i.description || '').trim());
    if (hasInvalid) {
      setError('Each intent must have both name and description. Please fill in all fields.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await aiSetupConfirmIntents(
        projectIdNum,
        intents.map((i) => ({
          key: i.key || '',
          display_name: (i.display_name || '').trim(),
          description: (i.description || '').trim(),
        })),
        sessionId ?? undefined
      );
      setSessionId(res.session_id);
      setStep(res.step as Step);
      setIntents(res.intents);
      setIntentDataQuestions(null);
      setStep4Answers({});
    } catch (e) {
      if (isStepConflictError(e)) {
        await handleStepConflict();
      } else if (axios.isAxiosError(e) && e.response?.status === 400) {
        const detail = (e.response?.data as { detail?: { message?: string } })?.detail;
        setError(detail?.message || 'Failed to confirm intents');
      } else {
        setError(e instanceof Error ? e.message : 'Failed to confirm intents');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStep4 = async () => {
    if (!projectIdNum) return;
    setLoading(true);
    setError(null);
    try {
      const qList = intentDataQuestions ?? [];
      const answers: Step4Answer[] = qList.map((_, i) => {
        const a = step4Answers[i];
        return {
          question_index: i,
          answer: a?.in_knowledge_base ? undefined : (a?.answer ?? undefined),
          skipped: a?.in_knowledge_base ? false : (a?.skipped ?? true),
          in_knowledge_base: a?.in_knowledge_base ?? false,
        };
      });
      const res = await aiSetupSubmitStep4(projectIdNum, answers, sessionId ?? undefined);
      setSessionId(res.session_id);
      setStep(res.step as Step);
      setIntentDataQuestions(null);
      setStep4Answers({});
    } catch (e) {
      if (isStepConflictError(e)) {
        await handleStepConflict();
      } else {
        setError(e instanceof Error ? e.message : 'Failed to submit');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStructureContext = async () => {
    if (!projectIdNum) return;
    setLoading(true);
    setError(null);
    try {
      const res = await aiSetupStructureContext(projectIdNum, sessionId ?? undefined);
      setSessionId(res.session_id);
      setStep(res.step as Step);
    } catch (e) {
      if (isStepConflictError(e)) {
        await handleStepConflict();
      } else {
        setError(e instanceof Error ? e.message : 'Failed to structure context');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!projectIdNum) return;
    setLoading(true);
    setError(null);
    try {
      const res = await aiSetupGenerate(projectIdNum, sessionId ?? undefined);
      setSessionId(res.session_id);
      setStep(res.step as Step);
      setBatchId(res.batch_id);
    } catch (e) {
      if (isStepConflictError(e)) {
        await handleStepConflict();
      } else {
        setError(e instanceof Error ? e.message : 'Failed to start generation');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectIdNum || !batchId || step !== 'generating_in_progress') return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const status = await aiSetupGetGenerationStatus(projectIdNum, batchId);
        if (cancelled) return;
        setBatchStatus(status);
        if (status.done) {
          setStep(status.all_success ? 'completed' : 'generation_failed');
        }
      } catch {
        // ignore poll errors
      }
    };
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [projectIdNum, batchId, step]);

  if (!projectIdNum) {
    return (
      <div className="container py-8">
        <div className="text-center text-red-600">Invalid project ID</div>
      </div>
    );
  }

  if (loading && step === 'idle') {
    return (
      <div className="container py-8">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <Link
            to={`/projects/${projectIdNum}/settings`}
            className="text-primary-600 hover:text-primary-700 text-sm mb-2 inline-block"
          >
            &larr; Back to Project Settings
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">AI Setup Wizard</h1>
          <p className="text-gray-600 mt-1">
            Configure intents and prompts for the Fast Router
          </p>
          {step !== 'idle' && step !== 'completed' && step !== 'cancelled' && step !== 'generation_failed' && (
            <p className="text-sm text-gray-500 mt-2">Step: {step.replace(/_/g, ' ')}</p>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="ml-3">
              <p className="text-red-800">{error}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={clearError}
            className="text-red-600 hover:text-red-700 text-sm ml-4"
          >
            Dismiss
          </button>
        </div>
      )}

      {step === 'idle' && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Start Setup</h2>
          <p className="text-gray-600 mb-6">
            Begin the wizard to configure intents and prompts based on your company description.
          </p>
          <button
            onClick={() => handleStart(false)}
            disabled={loading}
            className="btn-primary px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Starting...' : 'Start Setup'}
          </button>
        </div>
      )}

      {step === 'analysis_interview' && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Describe Your Company</h2>
          {questions === null ? (
            <>
              <p className="text-gray-600 mb-4">
                Enter a description of your company, products, and services.
              </p>
              <div className="flex gap-2">
                <textarea
                  value={inputValue}
                  onChange={(e) => { setInputValue(e.target.value); clearError(); }}
                  placeholder="Your company description..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 min-h-[80px]"
                  rows={3}
                  disabled={loading}
                />
                <button
                  onClick={handleAnalyze}
                  disabled={loading || !inputValue.trim()}
                  className="btn-primary self-end px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Analyzing...' : 'Analyze'}
                </button>
              </div>
            </>
          ) : (
            <>
              {chatHistory.length > 0 && (
                <div className="space-y-2 mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-600">Your description:</p>
                  <p className="text-sm text-gray-700">{chatHistory[0]?.content}</p>
                </div>
              )}
              <p className="text-gray-600 mb-4">All questions are optional. You can skip any question.</p>
              {questions.length > 0 ? (
                <div className="space-y-4 mb-6">
                  {questions.map((q, i) => (
                    <div key={i} className="p-4 border border-gray-200 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-2">{q}</p>
                      <div className="flex gap-2 items-center flex-wrap">
                        <input
                          type="text"
                          value={step2Answers[i]?.skipped ? '' : (step2Answers[i]?.answer ?? '')}
                          onChange={(e) =>
                            setStep2Answers((prev) => ({
                              ...prev,
                              [i]: { answer: e.target.value, skipped: false },
                            }))
                          }
                          onFocus={() => {
                            if (step2Answers[i]?.skipped) {
                              setStep2Answers((prev) => ({ ...prev, [i]: { answer: '', skipped: false } }));
                            }
                          }}
                          placeholder="Your answer..."
                          className="flex-1 min-w-[200px] px-2 py-1.5 border border-gray-300 rounded text-sm"
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setStep2Answers((prev) => ({
                              ...prev,
                              [i]: { answer: '', skipped: true },
                            }))
                          }
                          className="text-gray-600 hover:text-gray-800 text-sm"
                        >
                          Skip
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 mb-4">No additional questions. You can proceed to intents.</p>
              )}
              <button
                onClick={handleSubmitStep2}
                disabled={loading}
                className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Continue'}
              </button>
            </>
          )}
        </div>
      )}

      {step === 'intent_proposal' && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Intents</h2>
          {intents.length === 0 ? (
            <div className="space-y-4">
              <p className="text-gray-600">Proposing intents based on your company profile...</p>
              <button
                onClick={handleProposeIntents}
                disabled={loading}
                className="btn-primary px-4 py-2 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Propose Intents'}
              </button>
            </div>
          ) : (
            <>
              <p className="text-gray-600 mb-4">
                Review and edit the intents below. Uncheck or remove any you don't need, then confirm.
              </p>
              <div className="space-y-4 mb-6">
                {intents.map((intent, idx) => (
                  <div key={intent.key || `intent-${idx}`} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <input
                          value={intent.display_name}
                          onChange={(e) =>
                            setIntents((prev) =>
                              prev.map((i, i2) =>
                                i2 === idx ? { ...i, display_name: e.target.value } : i
                              )
                            )
                          }
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-medium mb-1"
                          placeholder="Display name"
                        />
                        <input
                          value={intent.description}
                          onChange={(e) =>
                            setIntents((prev) =>
                              prev.map((i, i2) =>
                                i2 === idx ? { ...i, description: e.target.value } : i
                              )
                            )
                          }
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-600"
                          placeholder="Description"
                        />
                        <span className="text-xs text-gray-400 mt-1 block">key: {intent.key || '(auto)'}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIntents((prev) => prev.filter((_, i) => i !== idx))}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleProposeIntents}
                  disabled={loading}
                  className="btn-secondary px-4 py-2 disabled:opacity-50"
                >
                  Regenerate
                </button>
                <button
                  type="button"
                  onClick={() => setIntents((prev) => [...prev, { key: '', display_name: '', description: '' }])}
                  className="btn-secondary px-4 py-2"
                >
                  Add intent
                </button>
                <button
                  onClick={handleConfirmIntents}
                  disabled={
                    loading ||
                    intents.length === 0 ||
                    intents.some((i) => !(i.display_name || '').trim() || !(i.description || '').trim())
                  }
                  className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Confirming...' : 'Confirm & Continue'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {step === 'intent_data_collection' && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Intent Refinement</h2>
          {intentDataQuestions === null ? (
            <p className="text-gray-600">{loading ? 'Preparing questions...' : 'Preparing...'}</p>
          ) : intentDataQuestions.length === 0 ? (
            <p className="text-gray-600">No additional questions. Structuring context...</p>
          ) : (
            <>
              <p className="text-gray-600 mb-4">All questions are optional. You can skip any or mark that information exists in the knowledge base.</p>
              <div className="space-y-4 mb-6">
                {intentDataQuestions.map((q, i) => (
                  <div key={i} className="p-4 border border-gray-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">{q}</p>
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2 items-center flex-wrap">
                        <input
                          type="text"
                          value={step4Answers[i]?.in_knowledge_base ? '' : (step4Answers[i]?.answer ?? '')}
                          onChange={(e) =>
                            setStep4Answers((prev) => ({
                              ...prev,
                              [i]: {
                                answer: e.target.value,
                                skipped: false,
                                in_knowledge_base: false,
                              },
                            }))
                          }
                          placeholder="Your answer..."
                          className="flex-1 min-w-[200px] px-2 py-1.5 border border-gray-300 rounded text-sm"
                          disabled={loading || step4Answers[i]?.in_knowledge_base}
                        />
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={step4Answers[i]?.in_knowledge_base ?? false}
                            onChange={(e) =>
                              setStep4Answers((prev) => ({
                                ...prev,
                                [i]: {
                                  answer: '',
                                  skipped: false,
                                  in_knowledge_base: e.target.checked,
                                },
                              }))
                            }
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm text-gray-600">Information exists in knowledge base</span>
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            setStep4Answers((prev) => ({
                              ...prev,
                              [i]: { answer: '', skipped: true, in_knowledge_base: false },
                            }))
                          }
                          className="text-gray-600 hover:text-gray-800 text-sm"
                        >
                          Skip
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={handleSubmitStep4}
                disabled={loading}
                className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Continue'}
              </button>
            </>
          )}
        </div>
      )}

      {step === 'structuring_context' && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Structuring Context</h2>
          <p className="text-gray-600">{loading ? 'Structuring dialogue...' : 'Preparing structured context...'}</p>
        </div>
      )}

      {step === 'generation_configuration' && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Generate Prompts & Rules</h2>
          <p className="text-gray-600 mb-6">
            Start automatic generation of prompts and rules for each selected intent.
          </p>
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="btn-primary px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Starting...' : 'Start Generation'}
          </button>
        </div>
      )}

      {step === 'generating_in_progress' && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Generating</h2>
          {batchStatus ? (
            <div className="space-y-2">
              <p className="text-gray-600">
                {batchStatus.completed + batchStatus.failed} / {batchStatus.expected} tasks completed
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all"
                  style={{
                    width: `${batchStatus.expected > 0 ? ((batchStatus.completed + batchStatus.failed) / batchStatus.expected) * 100 : 0}%`,
                  }}
                />
              </div>
              {batchStatus.failed > 0 && (
                <p className="text-amber-600 text-sm">{batchStatus.failed} failed</p>
              )}
            </div>
          ) : (
            <p className="text-gray-600">Starting generation...</p>
          )}
        </div>
      )}

      {(step === 'completed' || step === 'generation_failed') && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {step === 'completed' ? 'Setup Complete' : 'Generation Completed with Errors'}
          </h2>
          <p className="text-gray-600 mb-6">
            {step === 'completed'
              ? 'Intents, rules, and prompts have been generated. The Fast Router is ready to use.'
              : 'Some tasks failed. You can retry or adjust intents and try again.'}
          </p>
          {batchStatus && (
            <p className="text-sm text-gray-500 mb-4">
              Completed: {batchStatus.completed} / {batchStatus.expected}
              {batchStatus.failed > 0 && `, Failed: ${batchStatus.failed}`}
            </p>
          )}
          <Link to={`/projects/${projectIdNum}/settings`} className="btn-primary inline-block px-6 py-2">
            Back to Project Settings
          </Link>
        </div>
      )}
    </div>
  );
};
